import React from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import styled from "styled-components";
import CardTitle from "../organisms/CardTitle";
import {
  MetodoPago,
  pagar,
  Pago,
  Venta,
} from "../../services/ventasServicios";
import SuccessButton from "../organisms/SuccessButton";
import { Controller, useForm } from "react-hook-form";
import CurrencyFormat from "react-currency-format";
import { currencyFormat, parseCurrency } from "../../utils/numberUtils";
import { useMutation } from "@tanstack/react-query";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import ServerErrorModal from "../organisms/ServerErrorModal";

interface Model {
  monto: string;
  metodo: MetodoPago;
  fecha: string;
}

const defaultValues: Model = {
  monto: "",
  metodo: "EFECTIVO",
  fecha: new Date().toString(),
};

export type PagarModalProps = {
  venta: Pick<Venta, "id" | "pendiente">;
  onRegistrarPago: () => void;
  onCloseClick: () => void;
};

function PagarModal({ venta, onRegistrarPago, onCloseClick }: PagarModalProps) {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<Model>({
    defaultValues,
    mode: "all",
  });

  const {
    error: errorMutacionPagar,
    isLoading: mutacionIsLoading,
    mutate,
  } = useMutation(pagar, {
    onSuccess: () => {
      onRegistrarPago();
    },
  });

  const onSubmit = (model: Model) => {
    const pago: Pago = {
      idVenta: venta.id,
      monto: parseCurrency(model.monto, "$", ","),
      fecha: new Date(model.fecha),
      metodo: model.metodo,
    };
    mutate(pago);
  };

  const error = errorMutacionPagar;

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <Modal show={true} centered>
      <StyledCard>
        <StyledCardHeader className="mt-3">
          <Col>
            <CardTitle>{"Pago o abono de venta"}</CardTitle>
          </Col>
        </StyledCardHeader>
        <StyledCardBody>
          <section>
            <Form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              validated={isSubmitted}
            >
              <Form.Group className="mb-3" controlId="formMonto">
                <Form.Label>Monto</Form.Label>
                <Controller
                  control={control}
                  name="monto"
                  defaultValue={""}
                  rules={{
                    required: true,
                    minLength: 1,
                    validate: (v) => {
                      const parsedVal = parseCurrency(v, "$", ",");
                      if (parsedVal > 0 && parsedVal <= venta.pendiente) {
                        return true;
                      }
                      return false;
                    },
                  }}
                  render={({
                    field: { onChange, onBlur, value, ref },
                    fieldState: { invalid, isTouched, isDirty },
                  }) => (
                    <CurrencyFormat
                      customInput={Form.Control}
                      ref={(_ref) => {
                        ref({
                          focus: () => {},
                        });
                      }}
                      value={value}
                      thousandSeparator={","}
                      onBlur={onBlur}
                      isInvalid={isTouched && invalid}
                      required
                      onChange={onChange}
                      prefix={"$"}
                      decimalSeparator={"."}
                      isValid={isDirty && isTouched && !invalid}
                    />
                  )}
                />

                <Form.Control.Feedback type="invalid">
                  Campo debe ser un número mayor a 0 y menor o igual a{" "}
                  {currencyFormat(venta.pendiente)}.
                </Form.Control.Feedback>
                <Form.Text>
                  {`Pendiente: ${currencyFormat(venta.pendiente)}`}
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Método de pago</Form.Label>
                <Controller
                  control={control}
                  name="metodo"
                  rules={{ required: true }}
                  render={({ field: { onChange, ref } }) => (
                    <>
                      <Form.Check
                        type="radio"
                        id="metodo_efectivo"
                        label="Efectivo"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        value={"EFECTIVO"}
                        required
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_nequi"
                        label="Nequi"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        value={"NEQUI"}
                        required
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_daviplata"
                        label="Daviplata"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"DAVIPLATA"}
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_transferencia"
                        label="Transferencia"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"TRANSFERENCIA"}
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_transferencia"
                        label="Tarjeta de Crédito"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"TARJETA_CREDITO"}
                        ref={ref}
                      />
                    </>
                  )}
                />
                {errors.metodo && (
                  <Form.Control.Feedback type="invalid">
                    {errors.metodo.type === "required" && (
                      <span>Requerido</span>
                    )}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                <Controller
                  control={control}
                  name="fecha"
                  rules={{
                    required: true,
                    minLength: 1,
                  }}
                  render={({
                    field: { onChange, onBlur, value, ref },
                    fieldState: { invalid },
                  }) => (
                    <Form.Control
                      ref={ref}
                      type="date"
                      value={value}
                      onChange={onChange}
                      isInvalid={invalid}
                      onBlur={onBlur}
                      required
                    />
                  )}
                />
                {errors.fecha && (
                  <Form.Control.Feedback type="invalid">
                    {errors.fecha.type === "required" && <span>Requerido</span>}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Row className="mt-5">
                <div
                  className="d-flex justify-content-end"
                  style={{ gap: "4px" }}
                >
                  <Button variant="danger" onClick={onCloseClick}>
                    <ClearIcon sx={{ fontSize: "18px" }} />
                    Cancelar
                  </Button>
                  <SuccessButton variant="success" type="submit">
                    {!mutacionIsLoading && (
                      <CheckIcon sx={{ fontSize: "18px" }} />
                    )}{" "}
                    {mutacionIsLoading && (
                      <Spinner animation="border" size="sm" />
                    )}
                    Registrar
                  </SuccessButton>
                </div>
              </Row>
            </Form>
          </section>
        </StyledCardBody>
      </StyledCard>
    </Modal>
  );
}

const StyledCard = styled(Card)`
  margin-bottom: 1.875rem;
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
  padding: 8px;
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background-color: inherit;
  display: flex;
  justify-content: start;
  align-items: center;
  border: 0px;
`;

export default PagarModal;
